import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import {
  PersonWeeklyPresencesDto,
  PRESENCE_UNDEFINED,
  PRESENCE_VACATION,
  WEEK_PRESENCE_REQUIRED,
} from '@src/types/Dto/Home'
import { Person } from '@src/types/Person'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface PresencesDataTableProps {
  persons: Person[]
  data: PersonWeeklyPresencesDto[] | null
}

const PresencesDataTable = ({ persons, data }: PresencesDataTableProps) => {
  const { t } = useTranslation()

  const personMap = useMemo(() => {
    const personMap = new Map<string, string>()
    persons.forEach((person) => {
      personMap.set(person.id, person.fullName)
    })
    return personMap
  }, [persons])

  const columns = useMemo(() => {
    return (
      <TableHead>
        <TableRow>
          {
            <TableCell
              key="person"
              align="left"
              sx={{
                width: 170,
                maxWidth: 170,
                minWidth: 170,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                top: 0,
                backgroundColor: (theme) => theme.palette.secondary.main,
              }}
            >
              {t('person.fullName')}
            </TableCell>
          }
          {
            <TableCell
              key="déficit"
              align="left"
              sx={{
                width: 60,
                maxWidth: 60,
                minWidth: 60,
                position: 'sticky',
                left: 170,
                zIndex: 2,
                top: 0,
                backgroundColor: (theme) => theme.palette.secondary.main,
              }}
            >
              {t('home.deficit')}
            </TableCell>
          }
          {data?.[0].weekPresencesCountList.map((week) => (
            <TableCell
              key={week.startOfWeekDate}
              align="left"
              sx={{
                width: 70,
                maxWidth: 70,
                minWidth: 70,
                position: 'sticky',
                zIndex: 1,
                top: 0,
                backgroundColor: (theme) => theme.palette.secondary.main,
              }}
            >
              {week.startOfWeekDate.slice(5, 10)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    )
  }, [data, t])

  const rows = useMemo(() => {
    if (!data) return []
    return data.map((item) => {
      const nameCells = []
      const weekCountCells = []
      const deficitCells = []
      const cells = []
      let presencesCount = 0
      let weekCount = 0
      nameCells.push(
        <TableCell
          key={`person-${item.personId}`}
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: 'white',
            size: 'small',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {personMap.get(item.personId) ?? t('person.unknown')}
        </TableCell>,
      )

      for (let i = 0; i < item.weekPresencesCountList.length; i++) {
        const currentWeekPresence = item.weekPresencesCountList[i]
        if (currentWeekPresence.presencesCount === PRESENCE_VACATION) {
          let colSpan = 1
          // adjust the cell span, based on the number of consecutive weeks with -1 presencesCount
          while (
            i + 1 < item.weekPresencesCountList.length &&
            item.weekPresencesCountList[i + 1].presencesCount === PRESENCE_VACATION
          ) {
            colSpan++
            i++
          }

          weekCountCells.push(
            <TableCell
              key={`${currentWeekPresence.startOfWeekDate}-merged`}
              colSpan={colSpan}
              sx={{
                textAlign: 'center',
                backgroundColor: 'lightBlue',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {'vacances'}
            </TableCell>,
          )
        } else if (currentWeekPresence.presencesCount == PRESENCE_UNDEFINED) {
          weekCountCells.push(<TableCell key={currentWeekPresence.startOfWeekDate}>{''}</TableCell>)
        } else if (currentWeekPresence.presencesCount < WEEK_PRESENCE_REQUIRED) {
          weekCount++
          presencesCount += currentWeekPresence.presencesCount
          weekCountCells.push(
            <TableCell key={currentWeekPresence.startOfWeekDate} sx={{ backgroundColor: 'lightRed' }}>
              {currentWeekPresence.presencesCount}
            </TableCell>,
          )
        } else {
          weekCount++
          presencesCount += currentWeekPresence.presencesCount
          weekCountCells.push(
            <TableCell key={currentWeekPresence.startOfWeekDate}>{currentWeekPresence.presencesCount}</TableCell>,
          )
        }
      }
      deficitCells.push(
        <TableCell
          key={`total-${item.personId}`}
          sx={{
            position: 'sticky',
            left: 170,
            zIndex: 1,
            backgroundColor: 'lightGray',
          }}
        >
          {presencesCount - weekCount * WEEK_PRESENCE_REQUIRED}
        </TableCell>,
      )
      cells.push(...nameCells)
      cells.push(...deficitCells)
      cells.push(...weekCountCells)
      return <TableRow key={item.personId}>{cells}</TableRow>
    })
  }, [data, personMap, t])
  return (
    <>
      <Paper variant="outlined" square={false} sx={{ height: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '100%' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            {columns}
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('common.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  )
}

export default PresencesDataTable
